import moment from "moment";

const DailyStats = (app) => {
  app
    /**
     * Add a comment to a progress-photo thread.
     * Body: { message, senderId, threadId?, dailyStatId?, photoType? }
     * If threadId omitted: creates thread, attaches to body photo (dailyStatId, photoType required).
     * Redirects back with threadId in query when a new thread was created.
     */
    .post(
      "/daily-stats/progress-photos/add-thread-comment",
      async ({ req, res, flash, api, assert }) => {
        const { body } = req;
        const { message, senderId, photoType, dailyStatId } = body;
        let { threadId } = body;
        let creatingNewThread = false;

        assert(senderId, 400, "Sender ID is required");
        assert(message, 400, "Message is required");

        try {
          if (!threadId) {
            creatingNewThread = true;
            assert(dailyStatId, 400, "Daily stat ID is required");
            assert(photoType, 400, "Photo type is required");

            const response = await api("thread", {
              method: "POST",
            });

            const data = await response.json();

            if (!data._id) {
              throw new Error("Failed to create thread");
            }

            threadId = data._id;

            await api("daily-stats-body-photos", {
              method: "PUT",
              body: {
                dailyStatId,
                photoType,
                threadId,
              },
            });
          }

          await api("comment", {
            method: "POST",
            body: {
              message,
              senderId,
              receiverId: body.receiverId,
              threadId,
              activityContext: body.activityContext,
            },
          });
          flash("success", "Successfully added comment");
          res.refreshWith(
            { ...(creatingNewThread && { threadId }) },
            { url: "back", hash: "#_" }
          );
        } catch (error) {
          flash("error", error.message);
          res.backDontScroll();
        }
      }
    )
    /**
     * Delete a comment from a progress-photo thread.
     * Params: _id (comment id). Body: { fullDelete?, threadId?, photoType?, dailyStatId? }
     * If fullDelete: detaches thread from body photo, deletes thread and all comments; body params required.
     * Otherwise: soft-deletes the single comment only.
     */
    .post(
      "/daily-stats/progress-photos/delete-thread-comment/:_id",
      async ({ req, res, flash, api, assert }) => {
        const { _id } = req.params;
        const { fullDelete, threadId, photoType, dailyStatId } = req.body;
        assert(_id, 400, "Comment ID is required");

        if (fullDelete) {
          assert(dailyStatId, 400, "Daily stat ID is required");
          assert(photoType, 400, "Photo type is required");
          assert(threadId, 400, "Thread ID is required");

          await api("daily-stats-body-photos", {
            method: "PUT",
            body: {
              dailyStatId,
              photoType,
              deleteThread: true,
            },
          });
          await api(`thread/${threadId}`, { method: "DELETE" });
          await api(`comment?threadId=${threadId}&fullDelete=true`, {
            method: "DELETE",
          });
          flash("success", "Successfully deleted thread");
          return res.refreshWith(
            { threadId: null },
            { url: "back", hash: "#_" }
          );
        }

        await api(`comment/${_id}`, { method: "DELETE" });
        flash("success", "Successfully deleted comment");
        return res.refreshWith({}, { url: "back", hash: "#_" });
      }
    )
    /**
     * Edit a comment in a progress-photo thread.
     * Body: { commentId, message }
     * PUTs the comment, then redirects back.
     */
    .post(
      "/daily-stats/progress-photos/edit-thread-comment",
      async ({ req, res, flash, api, assert }) => {
        const { body } = req;
        const { commentId, message } = body;

        assert(commentId, 400, "Comment ID is required");
        assert(message, 400, "Message is required");

        await api(`comment/${commentId}`, {
          method: "PUT",
          body: { message, editedAt: new Date() },
        });

        flash("success", "Comment saved successfully");

        res.refreshWith({}, { url: "back", hash: "#_" });
      }
    )
    /**
     * Add a comment to a meal-photo thread.
     * Body: { message, senderId, threadId?, mealPhotoId?, athleteId? }
     * If threadId omitted: creates thread, attaches to meal photo (mealPhotoId, athleteId required).
     * Redirects back with threadId in query when a new thread was created.
     */
    .post(
      "/daily-stats/meal-photos/add-thread-comment",
      async ({ req, res, flash, api, assert }) => {
        const { body } = req;
        const { message, senderId, mealPhotoId, athleteId, mealType, date } =
          body;
        let { threadId } = body;
        let creatingNewThread = false;

        assert(senderId, 400, "Sender ID is required");
        assert(message, 400, "Message is required");

        try {
          if (!threadId) {
            creatingNewThread = true;
            assert(mealPhotoId, 400, "Meal photo ID is required");
            assert(athleteId, 400, "Athlete ID is required");

            const response = await api("thread", {
              method: "POST",
            });

            const data = await response.json();

            if (!data._id) {
              throw new Error("Failed to create thread");
            }

            threadId = data._id;

            // Format date to YYYYMMDD if it's not already
            // dayDate from API is already a number in YYYYMMDD format, date from route is a string
            const formattedDate =
              typeof date === "number"
                ? date.toString()
                : moment(date).format("YYYYMMDD");

            const body = {
              photoIds: mealPhotoId,
              mealType,
              proxyAthlete: athleteId,
              threadId,
              date: formattedDate,
            };

            await api("meal-photos", {
              method: "PUT",
              body,
            });
          }

          await api("comment", {
            method: "POST",
            body: {
              message,
              senderId,
              receiverId: body.receiverId,
              threadId,
              activityContext: body.activityContext,
            },
          });
          flash("success", "Successfully added comment");
          res.refreshWith(
            { ...(creatingNewThread && { threadId }) },
            { url: "back", hash: "#_" }
          );
        } catch (error) {
          flash("error", error.message);
          res.backDontScroll();
        }
      }
    )
    /**
     * Delete a comment from a meal-photo thread.
     * Params: _id (comment id). Body: { fullDelete?, threadId?, mealPhotoId?, athleteId? }
     * If fullDelete: detaches thread from meal photo, deletes thread and all comments; body params required.
     * Otherwise: soft-deletes the single comment only.
     */
    .post(
      "/daily-stats/meal-photos/delete-thread-comment/:_id",
      async ({ req, res, flash, api, assert }) => {
        const { _id } = req.params;
        const { fullDelete, threadId, mealPhotoId, athleteId, date, mealType } =
          req.body;
        try {
          assert(_id, 400, "Comment ID is required");

          if (fullDelete) {
            assert(mealPhotoId, 400, "Meal photo ID is required");
            assert(athleteId, 400, "Athlete ID is required");
            assert(threadId, 400, "Thread ID is required");

            const formattedDate =
              typeof date === "number"
                ? date.toString()
                : moment(date).format("YYYYMMDD");

            const body = {
              photoIds: mealPhotoId,
              mealType,
              proxyAthlete: athleteId,
              date: formattedDate,
              deleteThread: true,
            };

            await api("meal-photos", {
              method: "PUT",
              body,
            });

            await api(`thread/${threadId}`, { method: "DELETE" });
            await api(`comment?threadId=${threadId}&fullDelete=true`, {
              method: "DELETE",
            });
            flash("success", "Successfully deleted thread");
            return res.refreshWith(
              { threadId: null },
              { url: "back", hash: "#_" }
            );
          }

          await api(`comment/${_id}`, { method: "DELETE" });
          flash("success", "Successfully deleted comment");
          return res.refreshWith({}, { url: "back", hash: "#_" });
        } catch (error) {
          flash("error", error.message);
          res.backDontScroll();
        }
      }
    )
    /**
     * Edit a comment in a meal-photo thread.
     * Body: { commentId, message }
     * PUTs the comment, then redirects back.
     */
    .post(
      "/daily-stats/meal-photos/edit-thread-comment",
      async ({ req, res, flash, api, assert }) => {
        const { body } = req;
        const { commentId, message } = body;

        assert(commentId, 400, "Comment ID is required");
        assert(message, 400, "Message is required");

        await api(`comment/${commentId}`, {
          method: "PUT",
          body: { message, editedAt: new Date() },
        });

        flash("success", "Comment saved successfully");

        res.refreshWith({}, { url: "back", hash: "#_" });
      }
    );
};

export default DailyStats;
